import React from 'react';

import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';

import { FULFILLMENT_CARRIER_LABEL } from '../status';
import type { FulfillmentCarrier, SubmitShippingPayload } from '../types';
import { FormField, TextArea, TextInput } from './form';

const CARRIERS: FulfillmentCarrier[] = ['DASH', 'LION_PARCEL', 'HANDOVER'];
const isCarrier = (v: string): v is FulfillmentCarrier =>
  (CARRIERS as string[]).includes(v);

/** "Serah terima langsung": who received the goods at the hub. */
export interface HandoverDraft {
  recipientName: string;
  note: string;
}

export const emptyHandover = (): HandoverDraft => ({
  recipientName: '',
  note: '',
});

/** Why Submit shipping is off for this choice (null = it may go). */
export const carrierBlocked = (
  carrier: FulfillmentCarrier,
  handover: HandoverDraft
): string | null =>
  carrier === 'HANDOVER' && !handover.recipientName.trim()
    ? 'Isi nama penerima.'
    : null;

/** The ship body for a choice (handover fields only for HANDOVER). */
export const shippingPayloadOf = (
  carrier: FulfillmentCarrier,
  handover: HandoverDraft
): SubmitShippingPayload =>
  carrier === 'HANDOVER'
    ? {
        carrier,
        handoverRecipientName: handover.recipientName.trim(),
        ...(handover.note.trim() ? { handoverNote: handover.note.trim() } : {}),
      }
    : { carrier };

/**
 * Who takes the goods at Submit shipping: Dash (the Shipments integration),
 * Lion Parcel (a third-party carrier whose AWB WMS issues and whose status
 * ops set by hand), or "Serah terima langsung" — handed over directly
 * (client pickup, hand carry): no shipment, no AWB, the order is Delivered at
 * once, with who received it. A transfer order still raises its inbound
 * request at the destination hub.
 */
export const CarrierChoice: React.FC<{
  value: FulfillmentCarrier;
  onChange: (carrier: FulfillmentCarrier) => void;
  handover?: HandoverDraft;
  onHandoverChange?: (handover: HandoverDraft) => void;
  /** A TRANSFER order: the destination hub's name (it still receives it). */
  transferTo?: string | null;
  disabled?: boolean;
}> = ({
  value,
  onChange,
  handover,
  onHandoverChange,
  transferTo = null,
  disabled,
}) => {
  const canHandover = !!onHandoverChange;
  const draft = handover ?? emptyHandover();
  return (
    <div className="flex flex-col gap-3" data-testid="carrier-choice">
      <FormField label="Kurir" required>
        <SegmentedControl
          value={value}
          onValueChange={(v: string) => {
            if (isCarrier(v)) onChange(v);
          }}
          aria-label="Kurir"
          className="w-full [&>*]:flex-1"
        >
          {CARRIERS.filter((c) => c !== 'HANDOVER' || !!onHandoverChange).map(
            (c) => (
              <SegmentedItem
                key={c}
                value={c}
                disabled={disabled || (c === 'HANDOVER' && !canHandover)}
                data-testid={`carrier-${c}`}
              >
                {FULFILLMENT_CARRIER_LABEL[c]}
              </SegmentedItem>
            )
          )}
        </SegmentedControl>
      </FormField>
      {value === 'HANDOVER' && canHandover && (
        <div className="flex flex-col gap-3" data-testid="handover-fields">
          <div
            className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2"
            data-testid="handover-info"
          >
            <p className="text-[13px] font-semibold text-text-strong-950">
              Langsung Delivered
            </p>
            <p className="mt-0.5 text-[12px] text-text-sub-600">
              {transferTo
                ? `Tanpa kurir & waybill. Stok dipotong; ${transferTo} tetap menerima lewat inbound request.`
                : 'Tanpa kurir & waybill. Stok dipotong, order langsung selesai.'}
            </p>
          </div>
          <FormField label="Nama penerima" required>
            <TextInput
              data-testid="handover-recipient"
              value={draft.recipientName}
              placeholder="Mis. Budi (kurir client)"
              disabled={disabled}
              onChange={(v) =>
                onHandoverChange?.({ ...draft, recipientName: v })
              }
            />
          </FormField>
          <FormField label="Catatan" optional>
            <TextArea
              data-testid="handover-note"
              value={draft.note}
              rows={2}
              placeholder="Mis. diambil sendiri di hub"
              disabled={disabled}
              onChange={(v) => onHandoverChange?.({ ...draft, note: v })}
            />
          </FormField>
        </div>
      )}
    </div>
  );
};
